'use client';

import { FiltersType, OfferType, ReferenceLocation, SearchTabs, Sorting } from '@/types';
import { ITEMS_PER_PAGE_KEY } from '@/constants';
import { useCallback, useMemo, useState } from 'react';
import {
  createParser,
  parseAsInteger,
  parseAsString,
  useQueryStates,
  type inferParserType,
} from 'nuqs';
import { lzCompress, lzDecompress } from '@/lib/lzString';

// Re-exported for backward compatibility (SSR usage in page.tsx files).
export { parseFilters, buildQs } from './filtersSerialization';

// ---------------------------------------------------------------------------
// nuqs parsers — one per URL key, mirrors filtersSerialization.ts logic
// ---------------------------------------------------------------------------

const parseAsNumArray = createParser({
  parse: (str) => {
    const arr = str.split(',').filter(Boolean).map(Number);
    return arr.length ? arr : null;
  },
  serialize: (arr: number[]) => arr.join(','),
});

const parseAsStrArray = createParser({
  parse: (str) => {
    const arr = str.split(',').filter(Boolean);
    return arr.length ? arr : null;
  },
  serialize: (arr: string[]) => arr.join(','),
});

// Category / Equipment items stored as comma-separated codes
const parseAsCodes = createParser({
  parse: (str) => {
    const codes = str.split(',').filter(Boolean);
    if (!codes.length) return null;
    return codes.map((code) => ({ code, title: '', count: 0 }));
  },
  serialize: (arr: Array<{ code: string }>) => [...new Set(arr.map((a) => a.code))].join(','),
});

const parseAsMapArea = createParser<NonNullable<FiltersType['mapArea']>>({
  parse: (str) => {
    if (!str) return null;
    if (str.includes('|')) {
      const lonLatGeometry = str.split('|').map((pt) => {
        const [lng, lat] = pt.split(',').map(Number);
        return [lng, lat] as [number, number];
      });
      const lngs = lonLatGeometry.map(([lng]) => lng);
      const lats = lonLatGeometry.map(([, lat]) => lat);
      return {
        lonLatGeometry,
        lonLatLeftBottom: [Math.min(...lngs), Math.min(...lats)] as [number, number],
        lonLatRightTop: [Math.max(...lngs), Math.max(...lats)] as [number, number],
      };
    }
    const parts = str.split(',').map(Number);
    if (parts.length === 4 && parts.every(isFinite)) {
      return {
        lonLatLeftBottom: [parts[0], parts[1]] as [number, number],
        lonLatRightTop: [parts[2], parts[3]] as [number, number],
      };
    }
    return null;
  },
  serialize: (area) => {
    const r = (n: number) => Math.round(n * 1e6) / 1e6;
    if (area.lonLatGeometry?.length) {
      return area.lonLatGeometry
        .map(([lng, lat]: [number, number]) => `${r(lng)},${r(lat)}`)
        .join('|');
    }
    if (area.lonLatLeftBottom && area.lonLatRightTop) {
      const [x1, y1] = area.lonLatLeftBottom;
      const [x2, y2] = area.lonLatRightTop;
      return `${r(x1)},${r(y1)},${r(x2)},${r(y2)}`;
    }
    return '';
  },
});

type AdmUnit = { id: string; name: string; parent_name: string };

const parseAsAdmunits = createParser<AdmUnit[]>({
  parse: (str) => {
    if (!str) return null;
    const ids = lzDecompress(str);
    if (!Array.isArray(ids) || !ids.length) return null;
    return (ids as string[]).map((id) => ({ id, name: '', parent_name: '' }));
  },
  serialize: (admunits) => lzCompress(admunits.map((a) => a.id)),
});

const parseAsLocations = createParser<ReferenceLocation[]>({
  parse: (str) => {
    if (!str) return null;
    const parsed = lzDecompress(str);
    if (!Array.isArray(parsed)) return null;
    return parsed as ReferenceLocation[];
  },
  serialize: (locs) =>
    locs?.length
      ? lzCompress(
          locs.map((loc) => ({
            type: loc.type,
            code: loc.code,
            name: loc.name,
          })),
        )
      : '',
});

// ---------------------------------------------------------------------------
// URL param schema
// ---------------------------------------------------------------------------
// Arrays are never referentially equal — eq must check emptiness for clearOnDefault to work
const emptyArrayEq = (a: unknown[], b: unknown[]) => a.length === 0 && b.length === 0;

const parseAsNumArrayClear = { ...parseAsNumArray, eq: emptyArrayEq };
const parseAsStrArrayClear = { ...parseAsStrArray, eq: emptyArrayEq };
const parseAsCodesClear = { ...parseAsCodes, eq: emptyArrayEq };
const parseAsLocationsClear = { ...parseAsLocations, eq: emptyArrayEq };

const filterParsers = {
  page: parseAsInteger.withDefault(0).withOptions({ clearOnDefault: true }),
  offerType: parseAsString.withDefault(OfferType.SELL).withOptions({ clearOnDefault: true }),
  categories: parseAsCodesClear.withDefault([]).withOptions({ clearOnDefault: true }),
  rooms: parseAsNumArrayClear.withDefault([]).withOptions({ clearOnDefault: true }),
  price: parseAsNumArrayClear.withDefault([]).withOptions({ clearOnDefault: true }),
  rentPrice: parseAsNumArrayClear.withDefault([]).withOptions({ clearOnDefault: true }),
  unitList: parseAsStrArrayClear.withDefault([]).withOptions({ clearOnDefault: true }),
  equipmentValue: parseAsCodesClear.withDefault([]).withOptions({ clearOnDefault: true }),
  areaValue: parseAsNumArrayClear.withDefault([]).withOptions({ clearOnDefault: true }),
  hwbClass: parseAsStrArrayClear.withDefault([]).withOptions({ clearOnDefault: true }),
  fgeeClass: parseAsStrArrayClear.withDefault([]).withOptions({ clearOnDefault: true }),
  locations: parseAsLocationsClear.withDefault([]).withOptions({ clearOnDefault: true }),
  perPage: parseAsInteger.withDefault(25).withOptions({ clearOnDefault: true }),
  tabs: parseAsString.withDefault(SearchTabs.LIST).withOptions({ clearOnDefault: true }),
  sorting: parseAsString.withDefault('MODIFICATION_DATE').withOptions({ clearOnDefault: true }),
  mapArea: parseAsMapArea,
  admunits: parseAsAdmunits,
  search: parseAsString,
};

type UrlState = inferParserType<typeof filterParsers>;

// Map URL state → FiltersType
function urlStateToFilters(s: UrlState, defaults?: Partial<FiltersType>): FiltersType {
  return {
    currentPage: s.page,
    offerTypeValue: s.offerType as OfferType,
    categoriesValue: s.categories,
    roomNumberValue: s.rooms,
    priceValue: s.price,
    rentPriceValue: s.rentPrice,
    unitListValue:
      defaults?.unitListValue !== undefined && s.unitList.length === 0
        ? defaults.unitListValue
        : s.unitList,
    equipmentValue: s.equipmentValue,
    areaValue: s.areaValue,
    powerConsumptionClasses: s.hwbClass,
    energyClassFactors: s.fgeeClass,
    locations: s.locations,
    itemsPerPage: s.perPage,
    tabs: s.tabs as SearchTabs,
    sorting: s.sorting as Sorting,
    mapArea: s.mapArea ?? undefined,
    admunits: s.admunits ?? undefined,
    keywords: s.search ?? undefined,
  };
}

// Map FiltersType patch → URL state patch
function filtersToUrlPatch(patch: Partial<FiltersType>): Partial<UrlState> {
  const out: Partial<UrlState> = {};
  if (patch.currentPage !== undefined) out.page = patch.currentPage;
  if (patch.offerTypeValue !== undefined) out.offerType = patch.offerTypeValue;
  if (patch.categoriesValue !== undefined) out.categories = patch.categoriesValue;
  if (patch.roomNumberValue !== undefined) out.rooms = patch.roomNumberValue;
  if (patch.priceValue !== undefined) out.price = patch.priceValue;
  if (patch.rentPriceValue !== undefined) out.rentPrice = patch.rentPriceValue;
  if (patch.unitListValue !== undefined) out.unitList = patch.unitListValue;
  if (patch.equipmentValue !== undefined) out.equipmentValue = patch.equipmentValue;
  if (patch.areaValue !== undefined) out.areaValue = patch.areaValue;
  if (patch.powerConsumptionClasses !== undefined) out.hwbClass = patch.powerConsumptionClasses;
  if (patch.energyClassFactors !== undefined) out.fgeeClass = patch.energyClassFactors;
  if (patch.locations !== undefined) out.locations = patch.locations;
  if (patch.itemsPerPage !== undefined) out.perPage = patch.itemsPerPage;
  if (patch.tabs !== undefined) out.tabs = patch.tabs as string;
  if (patch.sorting !== undefined) out.sorting = patch.sorting as string;
  if ('mapArea' in patch) out.mapArea = patch.mapArea ?? null;
  if ('admunits' in patch) out.admunits = patch.admunits ?? null;
  if ('keywords' in patch) out.search = patch.keywords ?? null;
  return out;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Returns true when no filter-related keys are present in the URL.
// nuqs clears keys that equal their default (clearOnDefault:true), so an "empty" URL
// means urlState equals EMPTY_URL_STATE on every filter field.
function isUrlEmpty(s: UrlState): boolean {
  return (
    s.page === 0 &&
    s.offerType === OfferType.SELL &&
    s.categories.length === 0 &&
    s.rooms.length === 0 &&
    s.price.length === 0 &&
    s.rentPrice.length === 0 &&
    s.unitList.length === 0 &&
    s.equipmentValue.length === 0 &&
    s.areaValue.length === 0 &&
    s.hwbClass.length === 0 &&
    s.fgeeClass.length === 0 &&
    s.locations.length === 0 &&
    s.perPage === 25 &&
    s.tabs === SearchTabs.LIST &&
    s.sorting === 'MODIFICATION_DATE' &&
    s.mapArea == null &&
    s.admunits == null &&
    s.search == null
  );
}

// ---------------------------------------------------------------------------
// Hook options
// ---------------------------------------------------------------------------
type UseFiltersParams = {
  options?: {
    syncUrl?: boolean;
    pathnameOverride?: string;
    defaults?: Partial<FiltersType>;
    // Predefined filters applied when the URL is empty (e.g. directlink page).
    // On the first user interaction they are merged into the URL together with the patch.
    initialFilters?: Partial<FiltersType>;
  };
};

const EMPTY_URL_STATE: UrlState = {
  page: 0,
  offerType: OfferType.SELL,
  categories: [],
  rooms: [],
  price: [],
  rentPrice: [],
  unitList: [],
  equipmentValue: [],
  areaValue: [],
  hwbClass: [],
  fgeeClass: [],
  locations: [],
  perPage: 25,
  tabs: SearchTabs.LIST,
  sorting: 'MODIFICATION_DATE',
  mapArea: null,
  admunits: null,
  search: null,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export const useFilters = ({ options = {} }: UseFiltersParams = {}) => {
  const { syncUrl = true, defaults, initialFilters } = options;

  // --- syncUrl: true — nuqs manages URL state with immediate local updates ---
  const [urlState, setUrlState] = useQueryStates(filterParsers, {
    shallow: true,
    history: 'replace',
  });

  // --- syncUrl: false — pure local state (home page search widget) ---
  const [localFilters, setLocalFilters] = useState<FiltersType>(() =>
    urlStateToFilters(EMPTY_URL_STATE, defaults),
  );

  // When initialFilters are provided and the URL is empty, use them as the active filter state
  // so the first render matches the SSR query key. The URL stays clean until the user interacts.
  const urlEmpty = syncUrl && !!initialFilters && isUrlEmpty(urlState);

  const filters = useMemo(() => {
    if (!syncUrl) return localFilters;
    const fromUrl = urlStateToFilters(urlState, defaults);
    if (urlEmpty) return { ...fromUrl, ...initialFilters };
    return fromUrl;
  }, [syncUrl, urlState, localFilters, defaults, urlEmpty, initialFilters]);

  const updateFilters = useCallback(
    (patch: Partial<FiltersType>) => {
      if (syncUrl) {
        // If the URL is still empty and initialFilters are set, write them to the URL together
        // with the patch so the full filter state is preserved after the first interaction.
        const base = urlEmpty && initialFilters ? filtersToUrlPatch(initialFilters) : {};
        void setUrlState({ ...base, ...filtersToUrlPatch(patch) });
      } else {
        setLocalFilters((prev) => ({ ...prev, ...patch }));
      }
    },
    [syncUrl, setUrlState, urlEmpty, initialFilters],
  );

  const defaultUnitList = defaults?.unitListValue ?? [];
  const unitListDirty =
    filters.unitListValue.length !== defaultUnitList.length ||
    filters.unitListValue.some((v, i) => v !== defaultUnitList[i]);

  const canReset =
    filters.categoriesValue.length > 0 ||
    filters.equipmentValue.length > 0 ||
    unitListDirty ||
    filters.areaValue.length > 0 ||
    filters.roomNumberValue.length > 0 ||
    filters.priceValue.length > 0 ||
    filters.rentPriceValue.length > 0 ||
    filters.powerConsumptionClasses.length > 0 ||
    filters.energyClassFactors.length > 0 ||
    (!!filters.locations && filters.locations.length > 0) ||
    filters.sorting !== 'MODIFICATION_DATE' ||
    !!filters.mapArea ||
    (!!filters.admunits && filters.admunits.length > 0) ||
    !!filters.keywords;

  const resetFilters = useCallback(() => {
    // perPage and tabs are intentionally preserved — user's view preference should survive reset
    const resetPatch: Partial<FiltersType> = {
      currentPage: 0,
      categoriesValue: [],
      equipmentValue: [],
      unitListValue: defaults?.unitListValue ?? [],
      areaValue: [],
      roomNumberValue: [],
      priceValue: [],
      rentPriceValue: [],
      powerConsumptionClasses: [],
      energyClassFactors: [],
      locations: [],
      sorting: 'MODIFICATION_DATE',
      mapArea: undefined,
      admunits: undefined,
      keywords: undefined,
    };
    if (syncUrl) {
      void setUrlState(filtersToUrlPatch(resetPatch));
    } else {
      setLocalFilters((prev) => ({ ...prev, ...resetPatch }));
    }
  }, [syncUrl, setUrlState, defaults]);

  const getResetUrl = useCallback(
    (targetPathname: string) => {
      const params = new URLSearchParams();
      if (filters.itemsPerPage && filters.itemsPerPage !== 25)
        params.set('perPage', String(filters.itemsPerPage));
      if (filters.tabs && filters.tabs !== SearchTabs.LIST) params.set('tabs', filters.tabs);
      if (filters.offerTypeValue && filters.offerTypeValue !== OfferType.SELL)
        params.set('offerType', filters.offerTypeValue);
      const qs = params.toString();
      return qs ? `${targetPathname}?${qs}` : targetPathname;
    },
    [filters.itemsPerPage, filters.tabs, filters.offerTypeValue],
  );

  const setItemsPerPage = useCallback(
    (value: number) => {
      window.scrollTo({ top: 0 });
      localStorage.setItem(ITEMS_PER_PAGE_KEY, String(value));
      updateFilters({ itemsPerPage: value, currentPage: 0 });
    },
    [updateFilters],
  );

  // getQueryString is used only in SearchFilter (syncUrl:false) to build navigation URLs
  const getQueryString = useCallback(
    (overrides?: Partial<FiltersType>) => {
      const next = { ...filters, ...overrides };
      const params = new URLSearchParams();
      const add = (k: string, v: string | undefined | null) => {
        if (v) params.set(k, v);
      };
      if (next.currentPage) add('page', String(next.currentPage));
      add('offerType', next.offerTypeValue);
      if (next.categoriesValue?.length)
        add('categories', [...new Set(next.categoriesValue.map((a) => a.code))].join(','));
      if (next.roomNumberValue?.length) add('rooms', next.roomNumberValue.join(','));
      if (next.priceValue?.length) add('price', next.priceValue.join(','));
      if (next.rentPriceValue?.length) add('rentPrice', next.rentPriceValue.join(','));
      if (next.admunits?.length) add('admunits', lzCompress(next.admunits.map((a) => a.id)));
      if (next.unitListValue?.length) add('unitList', next.unitListValue.join(','));
      if (next.locations?.length)
        add(
          'locations',
          lzCompress(
            next.locations.map((loc) => ({
              type: loc.type,
              code: loc.code,
              name: loc.name,
              latitude: loc.latitude,
              longitude: loc.longitude,
            })),
          ),
        );
      if (next.tabs && next.tabs !== SearchTabs.LIST) add('tabs', next.tabs);
      const qs = params.toString();
      return qs ? `?${qs}` : '';
    },
    [filters],
  );

  return {
    filters,
    setFilters: updateFilters,
    resetFilters,
    getResetUrl,
    canReset,
    setItemsPerPage,
    getQueryString,
  };
};
